import styled from "@emotion/styled";
import type { Theme } from "@emotion/react";
import { MDSText } from "@/mds/components/text";
import { Button } from "react-aria-components";
import { MDSCardVariant } from "./card.types";

const sharedStyles = ({ theme, variant }: { theme: Theme; variant: MDSCardVariant }) => {
  const background =
    variant === MDSCardVariant.Chrome
      ? theme.colors.background.input.chrome.default
      : theme.colors.background.card.default;
  const borderColor =
    variant === MDSCardVariant.Chrome ? theme.colors.border.surface : theme.colors.border.default;
  const borderRadius =
    variant === MDSCardVariant.Chrome ? theme.borderRadius.xlarge : theme.borderRadius.medium;
  const boxShadow = variant === MDSCardVariant.Chrome ? "none" : theme.shadows.surface;
  const padding = variant === MDSCardVariant.Chrome ? theme.spacing["7"] : theme.spacing["4"];

  return `
  display: block;

  border-radius: ${borderRadius};
  background: ${background};
  padding: ${padding};
  box-shadow: ${boxShadow};
  border: 1px solid ${borderColor};
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:disabled, &[disabled] {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;
    border-color: ${borderColor};
    background: ${background};
    transform: none;
    pointer-events: none;
  }
`;
};

export const StyledCard = styled.div<{ $variant: MDSCardVariant }>`
  ${({ theme, $variant }) => sharedStyles({ theme, variant: $variant })}
`;

export const StyledButton = styled(Button)<{ $variant: MDSCardVariant }>`
  ${({ theme, $variant }) => sharedStyles({ theme, variant: $variant })}
  cursor: pointer;

  &[data-hovered]:not([data-disabled]) {
    background: ${({ theme, $variant }) =>
      $variant === MDSCardVariant.Chrome
        ? theme.colors.background.input.chrome.hover
        : theme.colors.background.card.emphasis};
  }

  &[data-pressed]:not([data-disabled]) {
    transform: translateY(1px);
  }

  &[data-focus-visible] {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 2px;
  }
`;

export const StyledButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  justify-content: left;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

export const StyledCardHeader = styled.div`
  display: flex;
  align-items: left;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

export const StyledCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.icon.primary};
  & svg {
    font-size: ${({ theme }) => theme.fontSizes["header-s"]};
  }
  margin-left: 2px;
`;

export const StyledCardTitle = styled(MDSText)`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes["header-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  line-height: ${({ theme }) => theme.lineHeights["header-s"]};
  text-align: left;
  flex: 1;
`;

export const StyledCardRightAddon = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StyledCardDescription = styled(MDSText)`
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  text-align: left;
  text-wrap: wrap;
`;

export const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  font-size: ${({ theme }) => theme.fontSizes["text-m"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: left;
`;
